/**
 * Phase 27.46 Phase 2 — Retail Program tab.
 *
 * Lists Retail Program Requests with role-scoped visibility, filters
 * (carrier / status / agent / date), the New Request action, and the
 * three per-carrier Excel download buttons (KR-only). Deep-links via
 * `?tab=retail-program&id=<request_id>` open the detail dialog.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Store, Search, Loader2, Download, ArrowRight, RefreshCw, MapPin,
} from 'lucide-react';
import { retailProgramAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import RetailRequestFormDialog from './RetailRequestFormDialog';
import RetailRequestDetailDialog from './RetailRequestDetailDialog';
import RetailLocationsDialog from './RetailLocationsDialog';

const CARRIER_META = {
  devoted:  { label: 'Devoted',  tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  solis:    { label: 'Solis',    tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  elevance: { label: 'Elevance', tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
};

const PROGRAM_META = {
  cvs:            { label: 'CVS' },
  table_top:      { label: 'Table Top' },
  marketing_site: { label: 'Marketing Site' },
};
const SPONSOR_LABELS = {
  kr: 'KR', devoted: 'Devoted', solis: 'Solis', elevance: 'Elevance',
};

const STATUS_META = {
  draft:               { label: 'Draft',              tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  submitted:           { label: 'Submitted',          tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  kr_review:           { label: 'KR Review',          tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  sent_to_carrier:     { label: 'Sent to Carrier',    tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  carrier_responded:   { label: 'Carrier Responded',  tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  partially_approved:  { label: 'Partially Approved', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  approved:            { label: 'Approved',           tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  declined:            { label: 'Declined',           tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  completed:           { label: 'Completed',          tone: 'bg-slate-200 text-slate-700 border-slate-300' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function RetailProgramTab() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ carrier: 'all', status: 'all', program_type: 'all', q: '' });
  const [showForm, setShowForm] = useState(false);
  const [showLocations, setShowLocations] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.carrier !== 'all') params.carrier = filters.carrier;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.program_type !== 'all') params.program_type = filters.program_type;
      if (filters.q) params.q = filters.q;
      const r = await retailProgramAPI.list(params);
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load retail requests');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Deep-link support: /marketing?tab=retail-program&id=<request_id>
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && searchParams.get('tab') === 'retail-program') {
      setDetailId(id);
    }
  }, [searchParams]);

  const closeDetail = () => {
    setDetailId(null);
    if (searchParams.get('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const downloadCarrier = async (carrier) => {
    setDownloading(carrier);
    try {
      const r = await retailProgramAPI.downloadCarrierExport(carrier);
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `retail-${carrier}-pending.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const rowCount = r.headers?.['x-row-count'];
      toast.success(
        rowCount
          ? `Downloaded ${carrier} — ${rowCount} slot(s)`
          : `Downloaded ${carrier} requests`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const totals = useMemo(() => {
    const c = { total: items.length, pending: 0, sent: 0, approved: 0 };
    items.forEach((r) => {
      if (['submitted', 'kr_review'].includes(r.status) && r.carrier) c.pending += 1;
      if (r.status === 'sent_to_carrier') c.sent += 1;
      if (['approved', 'partially_approved'].includes(r.status)) c.approved += 1;
    });
    return c;
  }, [items]);

  return (
    <div data-testid="retail-program-tab" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Store size={18} className="text-emerald-600" /> Retail Program
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            CVS carrier assignments plus Table Top &amp; Marketing Site programs.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {isKR && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLocations(true)}
                data-testid="retail-manage-locations-btn"
              >
                <MapPin size={14} className="mr-1" /> Manage Locations
              </Button>
              {['devoted', 'solis', 'elevance'].map((c) => (
                <Button
                  key={c}
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCarrier(c)}
                  disabled={downloading === c}
                  data-testid={`retail-download-${c}`}
                >
                  {downloading === c
                    ? <Loader2 size={14} className="mr-1 animate-spin" />
                    : <Download size={14} className="mr-1" />}
                  {CARRIER_META[c].label} Requests
                </Button>
              ))}
            </>
          )}
          <Button
            onClick={() => setShowForm(true)}
            data-testid="retail-new-btn"
          >
            <Plus size={14} className="mr-1" /> New Retail Request
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: 'total', label: 'Total requests', value: totals.total, tone: 'bg-slate-50 border-slate-200' },
          { key: 'pending', label: 'Awaiting carrier send', value: totals.pending, tone: 'bg-amber-50 border-amber-200' },
          { key: 'sent', label: 'Sent to carrier', value: totals.sent, tone: 'bg-blue-50 border-blue-200' },
          { key: 'approved', label: 'Approved / Partial', value: totals.approved, tone: 'bg-emerald-50 border-emerald-200' },
        ].map((k) => (
          <div key={k.key} className={`rounded-lg border ${k.tone} px-3 py-2`} data-testid={`retail-kpi-${k.key}`}>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k.label}</p>
            <p className="text-2xl font-bold text-slate-900">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
              <Input
                className="pl-7"
                placeholder="Store address, city, ZIP, or agent name"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                data-testid="retail-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Program</Label>
            <Select value={filters.program_type} onValueChange={(v) => setFilters({ ...filters, program_type: v })}>
              <SelectTrigger className="w-[150px]" data-testid="retail-program-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All programs</SelectItem>
                {Object.entries(PROGRAM_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Carrier</Label>
            <Select value={filters.carrier} onValueChange={(v) => setFilters({ ...filters, carrier: v })}>
              <SelectTrigger className="w-[160px]" data-testid="retail-carrier-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All carriers</SelectItem>
                {Object.entries(CARRIER_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-[180px]" data-testid="retail-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="retail-refresh">
            <RefreshCw size={14} />
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-500">
              <Loader2 className="animate-spin inline mr-2" size={16} />Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="retail-empty">
              <Store size={30} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">
                No retail requests yet. Click <span className="font-medium">New Retail Request</span> to start.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Program</th>
                    <th className="text-left px-3 py-2 font-semibold">Agent</th>
                    <th className="text-left px-3 py-2 font-semibold">NPN</th>
                    <th className="text-left px-3 py-2 font-semibold">Location</th>
                    <th className="text-center px-3 py-2 font-semibold">Slots</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                    <th className="text-left px-3 py-2 font-semibold">Submitted</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((r) => {
                    const cm = CARRIER_META[r.carrier] || { label: r.carrier, tone: '' };
                    const sm = STATUS_META[r.status] || { label: r.status, tone: '' };
                    const prog = PROGRAM_META[r.program_type || 'cvs'] || { label: r.program_type };
                    const sponsorLabel = SPONSOR_LABELS[r.sponsor] || cm.label;
                    return (
                      <tr
                        key={r.request_id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setDetailId(r.request_id)}
                        data-testid={`retail-row-${r.request_id}`}
                      >
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] border font-medium">
                            {prog.label}
                          </Badge>
                          <div className="text-[11px] text-slate-500 mt-0.5">{sponsorLabel}</div>
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {r.assigned_agent_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{r.assigned_agent_npn || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">
                          <div className="truncate max-w-[220px]" title={r.location_name || r.store_address}>
                            {r.location_name || r.store_address}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {r.city}, {r.state} {r.zip_code}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="font-medium text-slate-900">{r.slot_count || 0}</span>
                          {r.approved_slot_count > 0 && (
                            <span className="text-emerald-600 ml-1 text-xs">
                              ({r.approved_slot_count} ok)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`${sm.tone} text-[10px] border font-medium`}>
                            {sm.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                          {fmtDate(r.created_at)}
                        </td>
                        <td className="px-2 py-2 text-slate-400"><ArrowRight size={14} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RetailRequestFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        onDone={load}
      />
      <RetailRequestDetailDialog
        requestId={detailId}
        isKR={isKR}
        onOpenChange={(open) => { if (!open) closeDetail(); }}
        onChanged={load}
      />
      <RetailLocationsDialog
        open={showLocations}
        onOpenChange={setShowLocations}
        onChanged={load}
      />
    </div>
  );
}
